import React, { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, IconButton, InputAdornment, Stack, Tooltip, Typography } from "@mui/material";
import { NumberInput } from "components/forms";
import { PolicyAssetClass, TargetPolicyItem, TargetPolicySaveRequest } from "store/types";

import { acKey, EPSILON, formatSigned, round1, subKey, useTargetPolicyState } from "./useTargetPolicyState";

const COLOR_PRIMARY = "#071f35";
const COLOR_SECONDARY = "#496682";
const COLOR_DISABLED = "#92a7bc";
const COLOR_STROKE_IDLE = "#7b92a9";
const COLOR_STROKE_DIVIDER = "#dbe8f5";
const COLOR_BG_INFO = "#eaf3fb";
const COLOR_INFO = "#1c6db5";
const COLOR_BG_SUBTLE = "#f6f8fc";
const COLOR_ERROR = "#d93e3e";
const COLOR_ERROR_BG = "#fcebea";
const COLOR_WARN_BG = "#fff7e6";
const COLOR_WARN_BORDER = "#f5b14b";
const COLOR_WARN_TEXT = "#7a4400";
const COLOR_SUCCESS_BG = "#e6f6ec";
const COLOR_SUCCESS_BORDER = "#7fc99a";
const COLOR_SUCCESS_TEXT = "#2e7d32";


interface Props {
  mode?: "create" | "edit";
  sliceName: string;
  assetClasses: PolicyAssetClass[];
  initialPolicy?: TargetPolicyItem[];
  savePolicy: (values: TargetPolicySaveRequest) => Promise<void>;
  onSubmit: (values: TargetPolicySaveRequest) => void;
  onCancel: () => void;
}

export const TargetPolicyPrompt: React.FC<Props> = ({
  mode = "create",
  sliceName,
  assetClasses,
  initialPolicy,
  savePolicy,
  onSubmit,
  onCancel
}) => {
  const isEdit = mode === "edit";

  const {
    expandedById,
    unallocated,
    overAllocated,
    canNormalize,
    setParent,
    commitParent,
    setSub,
    commitSub,
    toggleExpanded,
    handleNormalize,
    handleEqualSplit,
    getParent,
    getSub,
    getRowInfo,
    buildResult
  } = useTargetPolicyState(assetClasses, initialPolicy);

  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      onCancel();
    };
    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [onCancel]);

  const handleSave = async () => {
    setIsSaving(true);
    setApiError(null);
    try {
      const result = buildResult();
      await savePolicy(result);
      onSubmit(result);
    } catch (e) {
      const message = e instanceof Error && e.message ? e.message : "Policy could not be saved. Try again.";
      setApiError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        outline: "none",
        width: 680,
        maxHeight: "calc(100vh - 64px)",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
        overflow: "hidden"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: "24px", py: "16px", borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}` }}
      >
        <Stack sx={{ minWidth: 0 }}>
          <Typography sx={{ color: COLOR_PRIMARY, fontSize: "18px", lineHeight: "24px", fontWeight: 500 }}>
            {isEdit ? "Edit Target Policy" : "Set Target Policy"}
          </Typography>
          <Typography
            sx={{
              color: COLOR_SECONDARY,
              fontSize: "13px",
              lineHeight: "18px",
              mt: "2px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            Slice: {sliceName} &middot; Saving will replace the current targets.
          </Typography>
        </Stack>
        <IconButton onClick={onCancel} sx={{ padding: "4px", color: COLOR_PRIMARY }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Stack sx={{ p: "16px 24px 8px", gap: "12px", flex: "0 0 auto" }}>
        <Stack
          direction="row"
          gap="8px"
          sx={{
            backgroundColor: COLOR_BG_INFO,
            border: `1px solid ${COLOR_INFO}33`,
            borderRadius: "6px",
            padding: "10px 12px"
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: COLOR_INFO, flexShrink: 0, mt: "1px" }} />
          <Typography sx={{ color: COLOR_PRIMARY, fontSize: "13px", lineHeight: "18px" }}>
            Sub-category targets are informational &mdash; parent % is the governed allocation.
          </Typography>
        </Stack>
      </Stack>

      <Stack sx={{ px: "24px", pb: "8px", flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
        <Stack sx={{ gap: "8px" }}>
          {assetClasses.length === 0 ? (
            <Typography
              sx={{ color: COLOR_DISABLED, fontSize: "14px", lineHeight: "20px", py: "16px", textAlign: "center" }}
            >
              No asset classes are active for this account.
            </Typography>
          ) : (
            assetClasses.map((parent) => {
              const pid = acKey(parent.type, parent.id);
              const subs = parent.subClasses ?? [];
              const { subTotal, subTotalMismatch, arrow } = getRowInfo(parent);
              const hasSubs = subs.length > 0;
              const expanded = hasSubs ? expandedById[pid] ?? false : false;

              return (
                <Stack
                  key={pid}
                  sx={{
                    border: `1px solid ${COLOR_STROKE_DIVIDER}`,
                    borderRadius: "6px",
                    backgroundColor: "white"
                  }}
                >
                  <PolicyRow
                    name={parent.name}
                    value={getParent(pid)}
                    onChange={(v) => setParent(pid, v)}
                    onBlur={() => commitParent(pid)}
                    weight={500}
                    leading={
                      hasSubs ? (
                        <IconButton
                          onClick={() => toggleExpanded(pid)}
                          sx={{ padding: "2px", color: COLOR_PRIMARY, mr: "4px" }}
                        >
                          {expanded ? (
                            <ExpandLessIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      ) : null
                    }
                  />
                  {hasSubs && expanded && (
                    <Stack
                      sx={{
                        borderTop: `1px solid ${COLOR_STROKE_DIVIDER}`,
                        backgroundColor: COLOR_BG_SUBTLE
                      }}
                    >
                      {subs.map((sub) => {
                        const sid = subKey(sub.id);
                        return (
                          <PolicyRow
                            key={sid}
                            name={sub.name}
                            value={getSub(sid)}
                            onChange={(v) => setSub(sid, v)}
                            onBlur={() => commitSub(sid)}
                            indented
                          />
                        );
                      })}
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ padding: "6px 12px 8px" }}
                      >
                        <SubTotalBadge mismatch={subTotalMismatch} value={subTotal} arrow={arrow} />
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              );
            })
          )}
        </Stack>
      </Stack>

      <Stack sx={{ px: "24px", pt: "8px", pb: "12px", gap: "8px", borderTop: `1px solid ${COLOR_STROKE_DIVIDER}` }}>
        <UnallocatedRow value={unallocated} />

        {overAllocated && (
          <Stack
            sx={{
              backgroundColor: COLOR_WARN_BG,
              border: `1px solid ${COLOR_WARN_BORDER}`,
              borderRadius: "6px",
              padding: "8px 12px"
            }}
          >
            <Typography sx={{ color: COLOR_WARN_TEXT, fontSize: "13px", lineHeight: "18px" }}>
              Targets exceed 100%. Adjust before saving.
            </Typography>
          </Stack>
        )}

        <Stack direction="row" gap="8px">
          <Tooltip title="Rescales all your values so they sum to 100%, preserving their relative weights.">
            <span>
              <Button
                variant="outlined"
                onClick={handleNormalize}
                disabled={!canNormalize}
                sx={{
                  textTransform: "none",
                  color: COLOR_PRIMARY,
                  borderColor: COLOR_STROKE_IDLE,
                  fontSize: "13px"
                }}
              >
                Normalize to 100%
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Divides 100% equally across all asset classes, then splits each asset class equally across its sub-categories.">
            <span>
              <Button
                variant="outlined"
                onClick={handleEqualSplit}
                sx={{
                  textTransform: "none",
                  color: COLOR_PRIMARY,
                  borderColor: COLOR_STROKE_IDLE,
                  fontSize: "13px"
                }}
              >
                Equal split
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack sx={{ px: "24px", py: "16px", borderTop: `1px solid ${COLOR_STROKE_DIVIDER}`, gap: "8px" }}>
        {apiError && (
          <Typography sx={{ color: COLOR_ERROR, fontSize: "14px", lineHeight: "20px" }}>{apiError}</Typography>
        )}
        <Stack direction="row" justifyContent="flex-end" gap="12px">
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ textTransform: "none", color: COLOR_PRIMARY, borderColor: COLOR_STROKE_IDLE }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || overAllocated}
            sx={{
              textTransform: "none",
              backgroundColor: COLOR_PRIMARY,
              "&:hover": { backgroundColor: "#0d2c4d" }
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

interface PolicyRowProps {
  name: string;
  value: number;
  onChange: (v: string) => void;
  onBlur?: () => void;
  indented?: boolean;
  weight?: number;
  leading?: React.ReactNode;
}

const PolicyRow: React.FC<PolicyRowProps> = ({ name, value, onChange, onBlur, indented, weight = 400, leading }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap="12px"
    sx={{
      padding: indented ? "6px 12px 6px 28px" : "8px 12px",
      borderBottom: indented ? `1px solid ${COLOR_STROKE_DIVIDER}` : "none"
    }}
  >
    {leading}
    <Typography
      sx={{
        flex: 1,
        minWidth: 0,
        color: COLOR_PRIMARY,
        fontSize: indented ? "13px" : "14px",
        lineHeight: "20px",
        fontWeight: weight,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {name}
    </Typography>
    <NumberInput
      name="policy-row"
      value={value}
      onChange={(e) => onChange((e.target as HTMLInputElement).value)}
      onBlur={onBlur}
      placeholder="0"
      size="small"
      sx={{ width: 110, flexShrink: 0, "& input": { textAlign: "right", padding: "6px 8px", fontSize: "13px" } }}
      numberInputProps={{ allowNegative: false }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px" }}>%</Typography>
          </InputAdornment>
        )
      }}
    />
  </Stack>
);

interface SubTotalBadgeProps {
  mismatch: boolean;
  value: number;
  arrow: string;
}

const SubTotalBadge: React.FC<SubTotalBadgeProps> = ({ mismatch, value, arrow }) => {
  const bg = mismatch ? COLOR_WARN_BG : "#eef2f7";
  const border = mismatch ? COLOR_WARN_BORDER : COLOR_STROKE_DIVIDER;
  const color = mismatch ? COLOR_WARN_TEXT : COLOR_SECONDARY;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: "12px",
        padding: "2px 10px"
      }}
    >
      <Typography sx={{ color, fontSize: "12px", lineHeight: "16px", fontWeight: 500 }}>
        sub-total {round1(value).toFixed(1)}%{arrow ? ` ${arrow}` : ""}
      </Typography>
    </Box>
  );
};

interface UnallocatedRowProps {
  value: number;
}

const UnallocatedRow: React.FC<UnallocatedRowProps> = ({ value }) => {
  const rounded = round1(value);
  const isZero = Math.abs(rounded) < EPSILON;
  const isPositive = rounded > EPSILON;
  const isNegative = rounded < -EPSILON;

  const bg = isZero ? COLOR_SUCCESS_BG : isPositive ? COLOR_WARN_BG : COLOR_ERROR_BG;
  const border = isZero ? COLOR_SUCCESS_BORDER : isPositive ? COLOR_WARN_BORDER : COLOR_ERROR;
  const color = isZero ? COLOR_SUCCESS_TEXT : isPositive ? COLOR_WARN_TEXT : COLOR_ERROR;
  // suppress unused var lint
  void isNegative;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        backgroundColor: bg,
        border: `1px solid ${border}`,
        borderRadius: "6px",
        padding: "8px 12px"
      }}
    >
      <Typography sx={{ color, fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}>Unallocated</Typography>
      <Typography sx={{ color, fontSize: "14px", lineHeight: "20px", fontWeight: 600 }}>
        {formatSigned(rounded)}
      </Typography>
    </Stack>
  );
};
